import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import {
  getSupabaseOrNull,
  isSupabaseClientInitialized,
} from '@litopia/shared/supabase';
import { useSharedAuth } from '@litopia/shared';
import { toast } from 'sonner';
import { logError } from '../utils/errorHandler';

const OPP_TYPES = ['opp_letter', 'opp_courier', 'opp_system', 'opp_upgrade'] as const;

type OppNotification = {
  id: string;
  recipient_id: string;
  type: string;
  entity_id: string | null;
  entity_type: string | null;
  title: string | null;
  message: string | null;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
};

interface OppNotificationContextType {
  notifications: OppNotification[];
  unreadCount: number;
  isLoading: boolean;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const OppNotificationContext = createContext<OppNotificationContextType | undefined>(undefined);

function getOppSupabaseOrNull() {
  return getSupabaseOrNull();
}

export const OppNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useSharedAuth();
  const userId = user?.id ?? null;
  const activeUserIdRef = useRef<string | null>(userId);
  activeUserIdRef.current = userId;
  const [snapshot, setSnapshot] = useState<{
    ownerId: string | null;
    notifications: OppNotification[];
    isLoading: boolean;
  }>({ ownerId: null, notifications: [], isLoading: false });
  const notifications = snapshot.ownerId === userId ? snapshot.notifications : [];
  const unreadCount = notifications.filter((notification) => !notification.is_read).length;
  const isLoading = snapshot.ownerId === userId ? snapshot.isLoading : false;

  const loadNotifications = useCallback(async (userId: string): Promise<OppNotification[]> => {
    if (!isSupabaseClientInitialized()) {
      return [];
    }

    if (!userId) {
      return [];
    }

    const supabaseClient = getOppSupabaseOrNull();
    if (!supabaseClient) return [];
    const { data, error } = await supabaseClient
      .from('notifications')
      .select('*')
      .eq('recipient_id', userId)
      .in('type', OPP_TYPES)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) throw error;
    return (data ?? []) as OppNotification[];
  }, []);

  const markRead = useCallback(async (id: string) => {
    const operationUserId = activeUserIdRef.current;
    if (!operationUserId || !isSupabaseClientInitialized()) {
      return;
    }

    try {
      const supabaseClient = getOppSupabaseOrNull();
      if (!supabaseClient) throw new Error('Supabase client is unavailable');
      const readAt = new Date().toISOString();
      const { error } = await supabaseClient
        .from('notifications')
        .update({ is_read: true, read_at: readAt })
        .eq('id', id)
        .eq('recipient_id', operationUserId);
      if (error) throw error;
      if (activeUserIdRef.current !== operationUserId) return;

      setSnapshot((prev) => prev.ownerId !== operationUserId ? prev : ({
        ...prev,
        notifications: prev.notifications.map((notification) =>
          notification.id === id
            ? { ...notification, is_read: true, read_at: readAt }
            : notification
        ),
      }));
    } catch (err) {
      logError(err, 'OppNotifications markRead');
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const operationUserId = activeUserIdRef.current;
    if (!operationUserId || !isSupabaseClientInitialized()) {
      return;
    }
    try {
      const supabaseClient = getOppSupabaseOrNull();
      if (!supabaseClient) throw new Error('Supabase client is unavailable');
      const readAt = new Date().toISOString();
      const { error } = await supabaseClient
        .from('notifications')
        .update({ is_read: true, read_at: readAt })
        .eq('recipient_id', operationUserId)
        .in('type', OPP_TYPES)
        .eq('is_read', false);
      if (error) throw error;
      if (activeUserIdRef.current !== operationUserId) return;

      setSnapshot((prev) => prev.ownerId !== operationUserId ? prev : ({
        ...prev,
        notifications: prev.notifications.map((notification) => ({
          ...notification,
          is_read: true,
          read_at: readAt,
        })),
      }));
    } catch (err) {
      logError(err, 'OppNotifications markAllRead');
    }
  }, []);

  useEffect(() => {
    setSnapshot({ ownerId: userId, notifications: [], isLoading: false });

    if (!userId) {
      return;
    }

    if (!isSupabaseClientInitialized()) {
      return;
    }

    let cancelled = false;
    setSnapshot({ ownerId: userId, notifications: [], isLoading: true });
    void loadNotifications(userId)
      .then((next) => {
        if (cancelled) return;
        if (activeUserIdRef.current !== userId) return;
        setSnapshot({ ownerId: userId, notifications: next, isLoading: false });
      })
      .catch((error) => {
        if (!cancelled) logError(error, 'OppNotifications fetch');
      })
      .finally(() => {
        if (!cancelled && activeUserIdRef.current === userId) {
          setSnapshot((prev) => prev.ownerId === userId ? { ...prev, isLoading: false } : prev);
        }
      });

    const supabaseClient = getOppSupabaseOrNull();
    if (!supabaseClient) return;
    const channelName = `opp-notifications:${userId}`;

    // Prevent duplicate channels (React StrictMode double-mount)
    const existingChannel = supabaseClient
      .getChannels()
      .find((channel) => channel.topic === `realtime:${channelName}`);
    if (existingChannel) supabaseClient.removeChannel(existingChannel);

    const channel: RealtimeChannel = supabaseClient
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          if (cancelled) return;
          const notif = payload.new as OppNotification;
          if (!OPP_TYPES.includes(notif.type as typeof OPP_TYPES[number])) return;

          if (activeUserIdRef.current !== userId) return;
          setSnapshot((prev) => prev.ownerId !== userId ? prev : ({
            ...prev,
            notifications: [notif, ...prev.notifications],
          }));

          toast(notif.title ?? '新通知', {
            description: notif.message ?? undefined,
            duration: 4000,
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          if (cancelled) return;
          const updated = payload.new as OppNotification;
          if (!OPP_TYPES.includes(updated.type as typeof OPP_TYPES[number])) return;

          if (activeUserIdRef.current !== userId) return;
          setSnapshot((prev) => prev.ownerId !== userId ? prev : ({
            ...prev,
            notifications: prev.notifications.map((notification) =>
              notification.id === updated.id ? { ...notification, ...updated } : notification
            ),
          }));
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabaseClient.removeChannel(channel);
    };
  }, [userId, loadNotifications]);

  return (
    <OppNotificationContext.Provider
      value={{ notifications, unreadCount, isLoading, markRead, markAllRead }}
    >
      {children}
    </OppNotificationContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useOppNotifications(): OppNotificationContextType {
  const ctx = useContext(OppNotificationContext);
  if (!ctx) {
    throw new Error('useOppNotifications must be used within OppNotificationProvider');
  }
  return ctx;
}
